import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import { api } from "../../api";
import usePageTitle from "../../hooks/usePageTitle";
import AppShell from "../AppShell";
import ImageWithFallback from "../ImageWithFallback";
import { usePriceVisibility } from "../../contexts/PriceVisibilityContext";
import {
  buildSportsSetImageCandidates,
  getSportsSetConfig,
  getSportsSetRoutePath,
  normalizeSportKey,
} from "../../utils/sportsSets";

const panelSx = {
  p: { xs: 2, md: 2.5 },
  borderRadius: 2.5,
  border: "1px solid",
  borderColor: "rgba(12, 35, 64, 0.08)",
  backgroundColor: "#ffffff",
  boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
};

const interactiveCardSx = {
  borderRadius: 2.25,
  border: "1px solid",
  borderColor: "rgba(12, 35, 64, 0.08)",
  backgroundColor: "#fbfcff",
  transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease, background-color 160ms ease",
  cursor: "pointer",
  textDecoration: "none",
  color: "inherit",
  display: "block",
  overflow: "hidden",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 16px 30px rgba(15, 23, 42, 0.12)",
    borderColor: "rgba(18, 89, 156, 0.20)",
    backgroundColor: "#ffffff",
  },
};

function PlaceholderImage({ label }) {
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "text.secondary",
        fontWeight: 700,
        letterSpacing: 0.3,
      }}
    >
      {label}
    </Box>
  );
}

function SportsSetCard({ set, sport }) {
  const { showPrices: pricePreferenceEnabled, pricesLoaded } = usePriceVisibility();
  const showPrices = pricesLoaded && pricePreferenceEnabled;
  const config = getSportsSetConfig(sport);
  const imageCandidates = useMemo(() => buildSportsSetImageCandidates(set, sport), [set, sport]);
  const useImage = Boolean(config.fallbackImages.length || set?.imageFile);

  return (
    <Box
      component={RouterLink}
      to={getSportsSetRoutePath(sport, set.slug)}
      sx={{
        ...interactiveCardSx,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          height: 220,
          flexShrink: 0,
          backgroundColor: "#eaf0f6",
          overflow: "hidden",
          borderBottom: "1px solid rgba(12, 35, 64, 0.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {useImage ? (
          <ImageWithFallback
            src={imageCandidates[0]}
            fallbackSrcs={imageCandidates.slice(1)}
            alt={set.set_name}
            style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center center", display: "block" }}
          />
        ) : (
          <PlaceholderImage label={config.shortTitle} />
        )}
      </Box>
      <Box sx={{ p: 1.6, flexGrow: 1, display: "flex", flexDirection: "column" }}>
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1.05, minHeight: 34, alignContent: "flex-start" }}>
          <Chip label={`${set.year || "TBD"}`} size="small" sx={{ fontWeight: 700 }} />
          <Chip label={`${set.item_count || 0} cards`} size="small" sx={{ fontWeight: 700, bgcolor: config.chipBg, color: "#10233f" }} />
          {showPrices && Number(set.priced_count || 0) ? <Chip label={`${set.priced_count} priced`} size="small" sx={{ fontWeight: 700 }} /> : null}
        </Stack>
        <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 900,
              color: "#10233f",
              mb: 0.4,
              lineHeight: 1.3,
              minHeight: 52,
            }}
          >
            {set.set_name}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.55, mt: "auto" }}>
            Browse the {config.shortTitle.toLowerCase()} set page with item cards and pricing.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default function SportsSetsPage({ sport }) {
  const routeParams = useParams();
  const routeSport = normalizeSportKey(sport || routeParams.sport || "");
  const config = getSportsSetConfig(routeSport);

  usePageTitle(config.title);

  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("newest");

  useEffect(() => {
    let mounted = true;
    const timeoutId = setTimeout(async () => {
      try {
        setLoading(true);
        setError("");
        const { data } = await api.get(`/api/sports/${routeSport}/sets`, {
          params: {
            search: String(searchTerm || "").trim(),
            sort: sortBy,
          },
        });
        if (!mounted) return;
        const nextSets = Array.isArray(data?.sets) ? data.sets : Array.isArray(data?.items) ? data.items : [];
        setSets(nextSets);
      } catch (err) {
        if (!mounted) return;
        setSets([]);
        setError(err?.response?.data?.error || err?.response?.data?.message || err?.message || `Failed to load ${config.title}.`);
      } finally {
        if (mounted) setLoading(false);
      }
    }, 200);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [routeSport, searchTerm, sortBy, config.title]);

  return (
    <AppShell>
      <Box sx={{ px: { xs: 0, md: 1 }, py: { xs: 1, md: 1.5 } }}>
        <Stack spacing={{ xs: 2.5, md: 3 }}>
          <Paper
            elevation={0}
            sx={{
              ...panelSx,
              p: { xs: 2.5, md: 3.25 },
              background: config.heroGradient,
              borderColor: "rgba(255,255,255,0.10)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "radial-gradient(circle at top right, rgba(255,255,255,0.15), transparent 30%), radial-gradient(circle at bottom left, rgba(255,255,255,0.10), transparent 26%)",
                pointerEvents: "none",
              }}
            />
            <Grid container spacing={3} sx={{ position: "relative", zIndex: 1 }} alignItems="center">
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    height: { xs: 220, md: 300 },
                    borderRadius: 2.25,
                    overflow: "hidden",
                    border: "1px solid rgba(255,255,255,0.12)",
                    backgroundColor: "rgba(255,255,255,0.12)",
                  }}
                >
                  {config.fallbackImages.length ? (
                    <ImageWithFallback
                      src={config.fallbackImages[0]}
                      fallbackSrcs={config.fallbackImages.slice(1)}
                      alt={config.title}
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                  ) : (
                    <PlaceholderImage label={config.shortTitle} />
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={8}>
                <Typography
                  variant="overline"
                  sx={{
                    display: "block",
                    color: "rgba(255,255,255,0.72)",
                    fontWeight: 800,
                    letterSpacing: 1.15,
                    mb: 1,
                  }}
                >
                  Sports Set Browse
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    color: "#ffffff",
                    fontWeight: 900,
                    lineHeight: 1.08,
                    mb: 1.1,
                    maxWidth: 760,
                    fontSize: { xs: "2rem", md: "2.7rem" },
                  }}
                >
                  {config.title}
                </Typography>
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.82)",
                    maxWidth: 860,
                    lineHeight: 1.65,
                    fontSize: { xs: "0.98rem", md: "1.02rem" },
                  }}
                >
                  Browse every {config.shortTitle.toLowerCase()} set we can group from the database, search by set name, and jump into individual set pages with cards and pricing.
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} sx={panelSx}>
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "space-between",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.5,
                mb: 2.25,
              }}
            >
              <TextField
                label="Search Sets"
                placeholder="Search by set name or year"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                fullWidth
              />
              <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 220 } }}>
                <InputLabel id={`${routeSport}-set-sort-label`}>Sort Sets</InputLabel>
                <Select
                  labelId={`${routeSport}-set-sort-label`}
                  value={sortBy}
                  label="Sort Sets"
                  onChange={(event) => setSortBy(event.target.value)}
                >
                  <MenuItem value="newest">Newest First</MenuItem>
                  <MenuItem value="oldest">Oldest First</MenuItem>
                  <MenuItem value="name_asc">Name A-Z</MenuItem>
                  <MenuItem value="name_desc">Name Z-A</MenuItem>
                  <MenuItem value="item_count">Most Items</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {loading ? (
              <Stack spacing={1.5} alignItems="center" justifyContent="center" sx={{ py: { xs: 5, md: 7 } }}>
                <CircularProgress size={34} />
                <Typography variant="body1" sx={{ color: "text.secondary" }}>
                  Loading {config.title.toLowerCase()}...
                </Typography>
              </Stack>
            ) : error ? (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            ) : (
              <>
                <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 700, mb: 2 }}>
                  {sets.length} {sets.length === 1 ? "Set" : "Sets"}
                </Typography>
                {sets.length ? (
                  <Grid container spacing={{ xs: 1.5, md: 2 }}>
                    {sets.map((set) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={set.slug} sx={{ display: "flex" }}>
                        <SportsSetCard set={set} sport={routeSport} />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    No {config.shortTitle.toLowerCase()} sets matched your search.
                  </Alert>
                )}
              </>
            )}
          </Paper>
        </Stack>
      </Box>
    </AppShell>
  );
}
